import React, { useMemo, useState, useCallback, useEffect } from "react"
import PropTypes from "prop-types"
import moment from "moment"
import { formatEventLabels } from "pagesV2/pro/Calendar/utils"
import ReservationDetailModal from "pagesV2/pro/Bookings/ReservationDetailPage/ReservationDetailModal"
import { useRefreshCalendarBookings } from "pagesV2/pro/Calendar/useRefreshCalendarBookings"
import useSearchFilter from "hooks/useSearchFilter"
import useListViewData from "pagesV2/pro/Calendar/hooks/useListViewData"
import { UNASSIGNED_ID, CALENDAR_TABS } from "pagesV2/pro/Calendar/constants"
import BookingList from "./BookingList"
import styles from "./index.module.css"

const ListViewGrid = ({ events, activeTab, crewIds, equipmentIds, categoryIds, roleIds, onChangeFilters }) => {
  const [params] = useSearchFilter({ accessAttributes: ["date"] })
  const today = moment().format("YYYY-MM-DD")
  const selectedDate = params.date || today

  useEffect(() => {
    if (onChangeFilters) onChangeFilters({ target: { name: "date", value: today } })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false)
  const [selectedBookingId, setSelectedBookingId] = useState(null)
  const refreshCalendarBookings = useRefreshCalendarBookings()

  const handleBookingClick = useCallback((id) => {
    setSelectedBookingId(id)
    setIsBookingModalOpen(true)
  }, [])

  const handleBookingCancel = () => { setIsBookingModalOpen(false); setSelectedBookingId(null) }
  const handleBookingCancelSuccess = () => { handleBookingCancel(); refreshCalendarBookings() }

  const { crewMembers, equipmentList } = useListViewData(events, activeTab, crewIds, equipmentIds, categoryIds, roleIds)

  const dayEvents = useMemo(
    () => events.filter((ev) => moment(ev.date).isSame(selectedDate, "day")).map(formatEventLabels),
    [events, selectedDate]
  )

  const showUnassigned = useMemo(
    () => activeTab === CALENDAR_TABS.CREW && (crewIds?.includes(UNASSIGNED_ID) || !crewIds?.length),
    [activeTab, crewIds]
  )

  const unassignedEvents = useMemo(
    () => dayEvents.filter((ev) => !ev.assigned_crew || ev.assigned_crew.length === 0),
    [dayEvents]
  )

  const isCrewAssignedToEvent = (ev, crew) =>
    (ev.assigned_crew || []).some((c) => c.profile_id === crew.id || c.id === crew.id || c.name === crew.name)

  const isEquipmentAssignedToEvent = (ev, eq) =>
    (ev.assigned_equipment || []).some((item) => item.name === eq.name || item.boat_id === eq.id)

  const eventsByCrewMember = useMemo(() => {
    if (activeTab !== CALENDAR_TABS.CREW) return {}
    return crewMembers.reduce((acc, crew) => {
      acc[crew.id] = dayEvents.filter((ev) => isCrewAssignedToEvent(ev, crew))
      return acc
    }, {})
  }, [activeTab, crewMembers, dayEvents])

  const eventsByEquipment = useMemo(() => {
    if (activeTab !== CALENDAR_TABS.EQUIPMENT) return {}
    return equipmentList.reduce((acc, eq) => {
      acc[eq.id] = dayEvents.filter((ev) => isEquipmentAssignedToEvent(ev, eq))
      return acc
    }, {})
  }, [activeTab, equipmentList, dayEvents])

  const resolveEvents = () => {
    if (activeTab === CALENDAR_TABS.CREW) {
      return [
        ...(showUnassigned ? unassignedEvents : []),
        ...crewMembers.flatMap((crew) => eventsByCrewMember[crew.id] || [])
      ]
    }
    if (activeTab === CALENDAR_TABS.EQUIPMENT) {
      return equipmentList.flatMap((eq) => eventsByEquipment[eq.id] || [])
    }
    return dayEvents
  }

  return (
    <>
      <div className={styles.listViewWrapper}>
        <div className={styles.listViewContent}>
          <BookingList events={resolveEvents()} onBookingClick={handleBookingClick} />
        </div>
      </div>
      {isBookingModalOpen && (
        <ReservationDetailModal
          open={isBookingModalOpen}
          bookingUuid={selectedBookingId}
          onClose={handleBookingCancelSuccess}
        />
      )}
    </>
  )
}

ListViewGrid.propTypes = {
  events: PropTypes.array.isRequired,
  activeTab: PropTypes.string,
  crewIds: PropTypes.array,
  equipmentIds: PropTypes.array,
  categoryIds: PropTypes.array,
  roleIds: PropTypes.array,
  onChangeFilters: PropTypes.func,
}

export default ListViewGrid
